import React, { useState } from "react";
import toast from "react-hot-toast";

import PasswordInput from "../../../components/auth/PasswordInput";
import PasswordStrength from "../../../components/auth/PasswordStrength";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { API } from "../../../constant/api";
import { PASSWORD_RULES } from "../../../utils/authSchemas";
import useAuthAxios from "../../../utils/baseAxios";
import { errorMessage } from "../../../utils/errorMessage";
import OtpConfirmDialog from "./OtpConfirmDialog";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Two steps: choose a password here, then confirm with an emailed OTP. The
 * password never leaves this component until the OTP comes back, so a
 * cancelled confirmation changes nothing.
 */
const ChangePasswordDialog = ({ open, onOpenChange }: Props) => {
  const axiosInstance = useAuthAxios();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otpOpen, setOtpOpen] = useState(false);

  const meetsRules = PASSWORD_RULES.every((rule) => rule.test(password));
  const matches = password === confirm;
  const canContinue = meetsRules && matches;

  const close = (next: boolean) => {
    if (!next) {
      setPassword("");
      setConfirm("");
    }
    onOpenChange(next);
  };

  const submit = async (otp: string) => {
    try {
      const { data } = await axiosInstance.patch(API.changePassword, {
        newPassword: password,
        otp,
      });
      toast.success(data?.message || "Password updated");
      setPassword("");
      setConfirm("");
      onOpenChange(false);
      return data;
    } catch (e: any) {
      toast.error(errorMessage(e?.response?.data?.message || e?.message));
      throw e;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="font-manrope mds:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-sora">Change password</DialogTitle>
            <DialogDescription>
              You'll confirm this with a code sent to your email.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (canContinue) setOtpOpen(true);
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <PasswordInput
                id="new-password"
                autoComplete="new-password"
                placeholder="Enter a new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <PasswordStrength password={password} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <PasswordInput
                id="confirm-password"
                autoComplete="new-password"
                placeholder="Re-enter the new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                aria-invalid={confirm.length > 0 && !matches}
              />
              {confirm.length > 0 && !matches && (
                <p className="text-[12px] font-medium text-destructive">
                  Passwords do not match
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={!canContinue}
                className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
              >
                Continue
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <OtpConfirmDialog
        open={otpOpen}
        onOpenChange={setOtpOpen}
        purpose="change-password"
        title="Confirm password change"
        description="Enter the 6-digit code we sent to your email to verify this action."
        confirmLabel="Change password"
        onConfirm={submit}
      />
    </>
  );
};

export default ChangePasswordDialog;
